/**
 * OPTIONAL Enviar account on THIS device (local-first).
 *
 * The device identity (signing and encryption keys) is generated here and
 * persisted in `localStorage` as JWK; the private ones NEVER travel to the server.
 * "Log out" = forget the local material (there is no recovery: it is an
 * anonymous account by design — no email or password).
 *
 * Flows:
 * - `createLocalAccount` — first device: creates an account + registers it.
 * - `startDeviceLink`/`completeDeviceLink` — NEW device of an existing
 *   account: generates identity, shows the link code (public material
 *   only) so a trusted device authorizes it, and checks
 *   with the address that the enrollment was done.
 * - `sendDeferredFiles` — SENDER (with or without account): encrypts and deposits envelopes.
 * - `collectEnvelope` — RECEIVER: collects, decrypts and acknowledges.
 */

import {
  buildAccountAddress,
  buildDeviceLinkCode,
  decodeDeferredPayload,
  type DeferredPlan,
  type DeviceIdentity,
  encodeDeferredPayload,
  exportDeviceIdentity,
  generateDeviceIdentity,
  importDeviceIdentity,
  isRecord,
  isStoredDeviceIdentity,
  openEnvelope,
  parseAccountAddress,
  planDeferredSends,
  sanitizeFilename,
  sealEnvelope,
  selectRecipientDevices,
  type StoredDeviceIdentity,
} from "@enviar/core";
import {
  ackEnvelope,
  depositEnvelope,
  type DeviceAuth,
  type EnvelopeMeta,
  fetchDevices,
  fetchEnvelope,
  registerAccount,
} from "@/lib/mailbox/client";

const ACCOUNT_KEY = "enviar.account.v1";
const PENDING_KEY = "enviar.pending-device.v1";

export interface LocalAccount {
  accountId: string;
  deviceId: string;
  deviceName: string;
  identity: DeviceIdentity;
}

export function accountAuth(account: LocalAccount): DeviceAuth {
  return { deviceId: account.deviceId, sigPrivateKey: account.identity.sigPrivateKey };
}

export function accountAddress(account: LocalAccount): string {
  return buildAccountAddress(account.accountId);
}

interface StoredAccount {
  v: 1;
  accountId: string;
  deviceName: string;
  identity: StoredDeviceIdentity;
}

function isStoredAccount(value: unknown): value is StoredAccount {
  return (
    isRecord(value) &&
    value.v === 1 &&
    typeof value.accountId === "string" &&
    typeof value.deviceName === "string" &&
    isStoredDeviceIdentity(value.identity)
  );
}

/** Account persisted on this device, or `null` (pure guest). */
export async function loadLocalAccount(): Promise<LocalAccount | null> {
  const raw = localStorage.getItem(ACCOUNT_KEY);
  if (raw === null) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isStoredAccount(parsed)) {
      return null;
    }
    const identity = await importDeviceIdentity(parsed.identity);
    return {
      accountId: parsed.accountId,
      deviceId: identity.deviceId,
      deviceName: parsed.deviceName,
      identity,
    };
  } catch {
    // Corrupt material: better a guest than a half-baked account.
    return null;
  }
}

async function persistAccount(
  accountId: string,
  deviceName: string,
  identity: DeviceIdentity,
): Promise<LocalAccount> {
  const stored: StoredAccount = {
    v: 1,
    accountId,
    deviceName,
    identity: await exportDeviceIdentity(identity),
  };
  localStorage.setItem(ACCOUNT_KEY, JSON.stringify(stored));
  return { accountId, deviceId: identity.deviceId, deviceName, identity };
}

/** Creates the account with THIS device as the first trusted device. */
export async function createLocalAccount(deviceName: string): Promise<LocalAccount> {
  const identity = await generateDeviceIdentity();
  const registered = await registerAccount({
    sigPub: identity.sigPub,
    encPub: identity.encPub,
    deviceName,
  });
  return persistAccount(registered.accountId, deviceName, identity);
}

/** Forgets the account ON THIS device (the local keys are destroyed). */
export function forgetLocalAccount(): void {
  localStorage.removeItem(ACCOUNT_KEY);
  localStorage.removeItem(PENDING_KEY);
}

// ─── Link this device to an existing account ─────────────────────────────────

interface PendingDevice {
  v: 1;
  deviceName: string;
  identity: StoredDeviceIdentity;
}

/**
 * Step 1 (on the NEW device): generates/reuses a pending identity and
 * returns the link code to paste into a trusted device.
 */
export async function startDeviceLink(deviceName: string): Promise<string> {
  const raw = localStorage.getItem(PENDING_KEY);
  if (raw !== null) {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (
        isRecord(parsed) &&
        parsed.v === 1 &&
        typeof parsed.deviceName === "string" &&
        isStoredDeviceIdentity(parsed.identity)
      ) {
        const identity = await importDeviceIdentity(parsed.identity);
        return buildDeviceLinkCode({
          sigPub: identity.sigPub,
          encPub: identity.encPub,
          name: parsed.deviceName,
        });
      }
    } catch {
      // corrupt pending identity → regenerate
    }
  }
  const identity = await generateDeviceIdentity();
  const pending: PendingDevice = {
    v: 1,
    deviceName,
    identity: await exportDeviceIdentity(identity),
  };
  localStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  return buildDeviceLinkCode({
    sigPub: identity.sigPub,
    encPub: identity.encPub,
    name: deviceName,
  });
}

/**
 * Step 2 (on the NEW device, after authorization): checks against the
 * address that this device is already trusted and activates the local account.
 */
export async function completeDeviceLink(address: string): Promise<LocalAccount | null> {
  const accountId = parseAccountAddress(address);
  const raw = localStorage.getItem(PENDING_KEY);
  if (accountId === null || raw === null) {
    return null;
  }
  let pending: PendingDevice;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      !isRecord(parsed) ||
      parsed.v !== 1 ||
      typeof parsed.deviceName !== "string" ||
      !isStoredDeviceIdentity(parsed.identity)
    ) {
      return null;
    }
    pending = { v: 1, deviceName: parsed.deviceName, identity: parsed.identity };
  } catch {
    return null;
  }
  const identity = await importDeviceIdentity(pending.identity);
  const devices = await fetchDevices(accountId);
  if (!devices.some((d) => d.deviceId === identity.deviceId)) {
    return null; // not yet authorized by a trusted device
  }
  localStorage.removeItem(PENDING_KEY);
  return persistAccount(accountId, pending.deviceName, identity);
}

// ─── Deferred send (sender, with or without account) ─────────────────────────

type DeferredSendResult =
  | { kind: "sent"; envelopes: number; devices: number }
  | { kind: "bad-address" }
  | Exclude<DeferredPlan, { kind: "ok" }>;

/**
 * Encrypts each file for EACH trusted device of the recipient and
 * deposits the envelopes in their mailbox. The relay only sees ciphertext.
 */
export async function sendDeferredFiles(
  address: string,
  files: File[],
  options?: { from?: string },
): Promise<DeferredSendResult> {
  const accountId = parseAccountAddress(address);
  if (accountId === null) {
    return { kind: "bad-address" };
  }
  const devices = selectRecipientDevices(await fetchDevices(accountId));
  const plan = planDeferredSends(
    files.map((f) => ({ name: f.name, size: f.size })),
    devices,
  );
  if (plan.kind !== "ok") {
    return plan;
  }
  // One framed payload per file (reused for all its devices).
  const framed = await Promise.all(
    files.map(async (file) => {
      const data = new Uint8Array(await file.arrayBuffer());
      return encodeDeferredPayload(
        {
          name: file.name,
          mime: file.type || "application/octet-stream",
          size: data.length,
          sentAtMs: Date.now(),
          ...(options?.from === undefined ? {} : { from: options.from }),
        },
        data,
      );
    }),
  );
  for (const send of plan.sends) {
    const payload = framed[send.fileIndex];
    if (payload === undefined) {
      continue;
    }
    const sealed = await sealEnvelope(send.device.encPub, payload);
    await depositEnvelope(accountId, send.device.deviceId, sealed);
  }
  return { kind: "sent", envelopes: plan.sends.length, devices: devices.length };
}

// ─── Collection (receiver with account) ──────────────────────────────────────

export interface CollectedFile {
  name: string;
  mime: string;
  size: number;
  url: string;
  from?: string;
}

/** Collects an envelope: decrypts it LOCALLY, acknowledges and offers it as a blob. */
export async function collectEnvelope(
  account: LocalAccount,
  meta: EnvelopeMeta,
): Promise<CollectedFile> {
  const auth = accountAuth(account);
  const payload = await fetchEnvelope(account.accountId, auth, meta.id);
  const opened = await openEnvelope(account.identity.encPrivateKey, payload);
  const { header, data } = decodeDeferredPayload(opened);
  const buffer = new ArrayBuffer(data.length);
  new Uint8Array(buffer).set(data);
  const blob = new Blob([buffer], { type: header.mime || "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  // Acknowledge AFTER decrypting correctly: if something fails, the envelope stays in the mailbox.
  await ackEnvelope(account.accountId, auth, meta.id);
  const collected: CollectedFile = {
    name: sanitizeFilename(header.name),
    mime: header.mime,
    size: header.size,
    url,
  };
  if (header.from !== undefined) {
    collected.from = header.from;
  }
  return collected;
}
